import React, { useEffect, useState } from 'react';
import { Radio, Select, DatePicker, Switch as AntSwitch } from 'antd';
import { IconBadge, TWButton, TWModal } from '../../../../components/ui';
import {
  BanknotesIcon,
  CreditCardIcon,
  PencilSquareIcon,
  TrashIcon,
  PlusIcon,
  LinkIcon,
  EyeIcon,
  CheckCircleIcon,
  BuildingLibraryIcon,
  UserIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import MasterCard from '../../../../assets/mastercard.svg';
import Visa from '../../../../assets/visa.svg';
import AmericanExpress from '../../../../assets/american-express.svg';
import Discover from '../../../../assets/discover.svg';
import ModalDelete from '../../../../components/modal-delete/modal-delete';
import { SkeletonBlock } from '../../../../components/skeleton/skeleton';
import bankAccountApi from '../../../../services/bankAccountApi';
import cardApi from '../../../../services/cardApi';
import AddressAutocomplete from '../../../../components/address-autocomplete/address-autocomplete';
import InputMask from 'react-input-mask';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

const inputClass =
  'block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400/20 disabled:cursor-not-allowed disabled:bg-slate-50 dark:disabled:bg-slate-900/20';

const SectionCard = ({
  icon: Icon,
  gradient,
  title,
  subtitle,
  action,
  children,
}) => (
  <section className="rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
    <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br ${gradient} text-white shadow-sm`}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {action}
    </div>
    <div className="px-6 py-6">{children}</div>
  </section>
);

const FieldLabel = ({ children, required }) => (
  <label className="mb-1.5 block text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
    {children}
    {required && <span className="ml-0.5 text-rose-500">*</span>}
  </label>
);

const ErrorText = ({ children }) =>
  children ? (
    <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">{children}</p>
  ) : null;

const PrimaryButton = ({ children, className = '', ...props }) => (
  <TWButton variant="primary" {...props} className={className}>
    {children}
  </TWButton>
);

const SecondaryButton = ({ children, className = '', ...props }) => (
  <TWButton variant="default" {...props} className={className}>
    {children}
  </TWButton>
);

const DangerIconButton = ({ onClick, disabled, loading }) => (
  <TWButton
    variant="danger"
    icon={TrashIcon}
    disabled={disabled}
    loading={loading}
    onClick={onClick}
  />
);

const AccountInformation = ({ companyDetail, setNotf }) => {
  const [bankAccountList, setBankAccountList] = useState([]);
  const [creditCardList, setCreditCardList] = useState([]);
  const [deletedBankAccount, setDeletedBankAccount] = useState({});
  const [deletedCreditCard, setDeletedCreditCard] = useState({});
  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });
  const [bankAccountForm, setBankAccountForm] = useState({
    id: '',
    accounttype: 10,
    accountholdername: '',
    routingnumber: '',
    accountnumber: '',
    isdefault: false,
    companyid: companyDetail.id,
    reprensentativefullname: '',
    taxid: '',
    last4ssn: '',
    reprensentativebirthday: '',
    city: '',
    state: '',
    postalcode: '',
    apartment: '',
    address: '',
    phone: '',
  });
  const [creditCardForm, setCreditCardForm] = useState({
    id: '',
    isdefault: false,
    createcardmodel: {
      expyear: 0,
      expmonth: 0,
      number: '',
      cvc: '',
      zipcode: '',
    },
    companyid: companyDetail.id,
  });
  const [dialogs, setDialogs] = useState({
    upsertBankAccount: false,
    viewBankAccount: false,
    upsertCreditCard: false,
    deleteBankAccount: false,
    deleteCreditCard: false,
  });
  const [errors, setErrors] = useState({});

  const getBankAccountList = async () => {
    setBankAccountList([]);
    try {
      const query = `?companyid=${companyDetail.id}`;
      const list = await bankAccountApi.list(query);
      if (list.data.data.length > 0) setBankAccountList(list.data.data);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const getCreditCardList = async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    setCreditCardList([]);
    try {
      const query = `?companyid=${companyDetail.id}`;
      const list = await cardApi.list(query);
      if (list.data.data.length > 0) setCreditCardList(list.data.data);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const onFormChangeCreditCard = (val, key) => {
    setCreditCardForm((prev) => ({
      ...prev,
      createcardmodel: { ...prev.createcardmodel, [key]: val },
    }));
  };

  const onClickAddBankAccount = () => {
    setBankAccountForm({
      id: '',
      accounttype: 10,
      accountholdername: '',
      routingnumber: '',
      accountnumber: '',
      companyid: companyDetail.id,
      reprensentativefullname: '',
      taxid: '',
      last4ssn: '',
      reprensentativebirthday: dayjs(),
      city: '',
      state: '',
      postalcode: '',
      apartment: '',
      address: '',
      phone: '',
    });
    setDialogs((prev) => ({ ...prev, upsertBankAccount: true }));
  };

  const onClickAddCreditCard = () => {
    setCreditCardForm({
      id: '',
      isdefault: false,
      createcardmodel: {
        expyear: 0,
        expmonth: 0,
        number: '',
        cvc: '',
        zipcode: '',
      },
      companyid: companyDetail.id,
    });
    setDialogs((prev) => ({ ...prev, upsertCreditCard: true }));
  };

  const onClickEditBankAccount = (val) => {
    setBankAccountForm({
      ...val,
      companyid: companyDetail.id,
      reprensentativebirthday: dayjs(val.reprensentativebirthday),
      confirmaccountnumber: val.accountnumber,
    });
    setDialogs((prev) => ({ ...prev, upsertBankAccount: true }));
  };

  const onClickViewBankAccount = (val) => {
    setBankAccountForm({
      ...val,
      companyid: companyDetail.id,
      reprensentativebirthday: dayjs(val.reprensentativebirthday),
      confirmaccountnumber: val.accountnumber,
    });
    setDialogs((prev) => ({ ...prev, viewBankAccount: true }));
  };

  const onFormSubmitBankAccount = async () => {
    if (
      !bankAccountForm.companyid ||
      !bankAccountForm.accountholdername ||
      !bankAccountForm.routingnumber ||
      !bankAccountForm.accountnumber ||
      !bankAccountForm.taxid ||
      !bankAccountForm.last4ssn ||
      !bankAccountForm.reprensentativefullname ||
      !bankAccountForm.reprensentativebirthday ||
      !bankAccountForm.city ||
      !bankAccountForm.state ||
      !bankAccountForm.postalcode ||
      !bankAccountForm.address ||
      !bankAccountForm.phone ||
      bankAccountForm.accountnumber !== bankAccountForm.confirmaccountnumber
    ) {
      setErrors((prev) => ({
        ...prev,
        companyid: !bankAccountForm.companyid,
        accountholdername: !bankAccountForm.accountholdername,
        routingnumber: !bankAccountForm.routingnumber,
        accountnumber: !bankAccountForm.accountnumber,
        taxid: !bankAccountForm.taxid,
        last4ssn: !bankAccountForm.last4ssn,
        reprensentativefullname: !bankAccountForm.reprensentativefullname,
        reprensentativebirthday: !bankAccountForm.reprensentativebirthday,
        city: !bankAccountForm.city,
        state: !bankAccountForm.state,
        postalcode: !bankAccountForm.postalcode,
        address: !bankAccountForm.address,
        phone: !bankAccountForm.phone,
        confirmaccountnumber:
          bankAccountForm.accountnumber !==
          bankAccountForm.confirmaccountnumber,
      }));
      return;
    }
    setErrors({});
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = { ...bankAccountForm };
      if (bankAccountList.length === 0) body.isdefault = true;
      await bankAccountApi.upsert(body);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      await getBankAccountList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, upsertBankAccount: false }));
    }
  };

  const onFormSubmitCreditCard = async () => {
    const zip = creditCardForm.createcardmodel.zipcode || '';
    if (
      !creditCardForm.companyid ||
      !creditCardForm.createcardmodel.expyear ||
      !creditCardForm.createcardmodel.expmonth ||
      !creditCardForm.createcardmodel.number ||
      !creditCardForm.createcardmodel.cvc ||
      creditCardForm.createcardmodel.number.length < 15 ||
      creditCardForm.createcardmodel.cvc.length < 3 ||
      zip.length < 5
    ) {
      setErrors((prev) => ({
        ...prev,
        companyid: !creditCardForm.companyid,
        expyear: !creditCardForm.createcardmodel.expyear,
        expmonth: !creditCardForm.createcardmodel.expmonth,
        zipcode: !zip || zip.length < 5,
        number:
          !creditCardForm.createcardmodel.number ||
          creditCardForm.createcardmodel.number.length < 15,
        cvc:
          !creditCardForm.createcardmodel.cvc ||
          creditCardForm.createcardmodel.cvc.length < 3,
      }));
      return;
    }
    setErrors({});
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = { ...creditCardForm };
      if (creditCardList.length === 0) body.isdefault = true;
      await cardApi.upsert(body);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      await getCreditCardList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, upsertCreditCard: false }));
    }
  };

  const onFormChangeBankAccount = (val, key) => {
    if (key === 'taxid') {
      const numbersOnly = String(val).replace(/\D/g, '');
      let formatted = '';
      for (let i = 0; i < numbersOnly.length; i++) {
        if (i === 2) formatted += '-';
        formatted += numbersOnly[i];
      }
      val = formatted;
    }
    setBankAccountForm((prev) => ({ ...prev, [key]: val }));
  };

  const onCloseUpsertBankAccount = () => {
    setErrors({});
    setDialogs((prev) => ({ ...prev, upsertBankAccount: false }));
  };

  const onCloseUpsertCreditCard = () => {
    setErrors({});
    setDialogs((prev) => ({ ...prev, upsertCreditCard: false }));
  };

  const onClickDeleteBankAccount = (bankAccount) => {
    if (bankAccount.isdefault) {
      setNotf({
        display: true,
        type: 'error',
        message: 'Cannot delete default bank account',
      });
      return;
    }
    setDeletedBankAccount(bankAccount);
    setDialogs((prev) => ({ ...prev, deleteBankAccount: true }));
  };

  const onClickDeleteCreditCard = (creditCard) => {
    if (creditCard.isdefault) {
      setNotf({
        display: true,
        type: 'error',
        message: 'Cannot delete default credit card',
      });
      return;
    }
    setDeletedCreditCard(creditCard);
    setDialogs((prev) => ({ ...prev, deleteCreditCard: true }));
  };

  const onConfirmDeleteBankAccount = async () => {
    setLoading((prev) => ({ ...prev, deleteLoading: true }));
    try {
      await bankAccountApi.deleteById(deletedBankAccount.id);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setDialogs((prev) => ({
        ...prev,
        deleteBankAccount: false,
        upsertBankAccount: false,
      }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
      await getBankAccountList();
    }
  };

  const onConfirmDeleteCreditCard = async () => {
    setLoading((prev) => ({ ...prev, deleteLoading: true }));
    try {
      await cardApi.deleteById(deletedCreditCard.id);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setDialogs((prev) => ({
        ...prev,
        deleteCreditCard: false,
        upsertCreditCard: false,
      }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
      await getCreditCardList();
    }
  };

  const onChangeDefaultBankAccount = async (bankAccount, val, index) => {
    if (val === false) {
      setNotf({
        display: true,
        type: 'error',
        message: 'Cannot uncheck default bank account',
      });
      return;
    }
    setLoading((prev) => ({ ...prev, upsertLoading: true }));
    const defaultAccount = bankAccountList.find((x) => x.isdefault === true);
    try {
      setBankAccountList((prev) => {
        const newList = [...prev];
        newList[index].isdefault = val;
        return newList;
      });
      if (defaultAccount) {
        setBankAccountList((prev) => {
          const newList = [...prev];
          const defaultIndex = newList.findIndex(
            (x) => x.id === defaultAccount.id,
          );
          newList[defaultIndex].isdefault = false;
          return newList;
        });
        await bankAccountApi.upsert({ ...defaultAccount, isdefault: false });
      }
      await bankAccountApi.upsert({ ...bankAccount, isdefault: val });
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      await getBankAccountList();
    }
  };

  const onChangeDefaultCreditCard = async (creditCard, val, index) => {
    if (val === false) {
      setNotf({
        display: true,
        type: 'error',
        message: 'Cannot uncheck default credit card',
      });
      return;
    }
    setLoading((prev) => ({ ...prev, upsertLoading: true }));
    const defaultCreditCard = creditCardList.find((x) => x.isdefault === true);
    try {
      setCreditCardList((prev) => {
        const newList = [...prev];
        newList[index].isdefault = val;
        return newList;
      });
      if (defaultCreditCard) {
        setCreditCardList((prev) => {
          const newList = [...prev];
          const defaultIndex = newList.findIndex(
            (x) => x.id === defaultCreditCard.id,
          );
          newList[defaultIndex].isdefault = false;
          return newList;
        });
        await cardApi.upsert({ ...defaultCreditCard, isdefault: false });
      }
      await cardApi.upsert({ ...creditCard, isdefault: val });
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      await getCreditCardList();
    }
  };

  const cardImage = (brand) => {
    switch (brand) {
      case 'Visa':
        return Visa;
      case 'MasterCard':
        return MasterCard;
      case 'American Express':
        return AmericanExpress;
      case 'Discover':
        return Discover;
      default:
        return Visa;
    }
  };

  const getThisYear = () => new Date().getFullYear();

  const expYearOptions = () => {
    const year = getThisYear();
    const options = [];
    for (let i = 0; i <= 10; i++)
      options.push({ label: year + i, value: year + i });
    return options;
  };

  const expMonthOptions = () => {
    const options = [];
    for (let i = 1; i <= 12; i++) options.push({ label: i, value: i });
    return options;
  };

  const onClickConnectStripe = async (account) => {
    setLoading((prev) => ({ ...prev, upsertLoading: true }));
    try {
      await bankAccountApi.connectStipeBankAccount(account.id);
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        display: true,
        message: error?.response?.data?.Message || 'Error',
        type: 'error',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      await getBankAccountList();
    }
  };

  const onAddressSelect = (obj) => {
    setBankAccountForm((prev) => ({
      ...prev,
      address: obj.street,
      city: obj.city,
      state: obj.state,
      postalcode: obj.zipCode,
      longitude: obj?.longitude || '',
      latitude: obj?.latitude || '',
      apartment: obj?.apartment || '',
    }));
  };

  useEffect(() => {
    getCreditCardList();
    getBankAccountList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyDetail.id]);

  const renderBankAccountFields = (disabled) => (
    <div className="space-y-5">
      {/* Account Type */}
      <div>
        <FieldLabel required={!disabled}>Account Type</FieldLabel>
        <Radio.Group
          disabled={disabled}
          onChange={(e) =>
            onFormChangeBankAccount(e.target.value, 'accounttype')
          }
          value={bankAccountForm.accounttype}
        >
          <Radio value={10}>Company</Radio>
          <Radio value={20} disabled>
            Individual
          </Radio>
        </Radio.Group>
      </div>

      {/* Legal Name + Tax ID */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel required={!disabled}>Legal Name</FieldLabel>
          <input
            disabled={disabled}
            value={bankAccountForm.accountholdername}
            onChange={(e) =>
              onFormChangeBankAccount(e.target.value, 'accountholdername')
            }
            type="text"
            className={inputClass}
            placeholder="Acme Inc"
          />
          <ErrorText>
            {errors.accountholdername && 'Legal Name can not be empty'}
          </ErrorText>
        </div>
        <div>
          <FieldLabel required={!disabled}>Tax ID</FieldLabel>
          <input
            disabled={disabled}
            value={bankAccountForm.taxid}
            onChange={(e) => onFormChangeBankAccount(e.target.value, 'taxid')}
            maxLength={10}
            type="text"
            className={inputClass}
            placeholder="12-3456789"
          />
          <ErrorText>{errors.taxid && 'Tax ID can not be empty'}</ErrorText>
        </div>
      </div>

      {/* Routing Number */}
      <div>
        <FieldLabel required={!disabled}>Routing Number</FieldLabel>
        <input
          disabled={disabled}
          value={bankAccountForm.routingnumber}
          onChange={(e) =>
            onFormChangeBankAccount(e.target.value, 'routingnumber')
          }
          type="number"
          className={inputClass}
          placeholder="1234567890"
        />
        <ErrorText>
          {errors.routingnumber && 'Routing Number can not be empty'}
        </ErrorText>
      </div>

      {/* Account + Confirm */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel required={!disabled}>Account Number</FieldLabel>
          <input
            disabled={disabled}
            value={bankAccountForm.accountnumber}
            onChange={(e) =>
              onFormChangeBankAccount(e.target.value, 'accountnumber')
            }
            type="number"
            className={inputClass}
            placeholder="1234567890"
          />
          <ErrorText>
            {errors.accountnumber && 'Account Number can not be empty'}
          </ErrorText>
        </div>
        <div>
          <FieldLabel required={!disabled}>Confirm Account Number</FieldLabel>
          <input
            disabled={disabled}
            value={bankAccountForm.confirmaccountnumber}
            onChange={(e) =>
              onFormChangeBankAccount(e.target.value, 'confirmaccountnumber')
            }
            type="number"
            className={inputClass}
            placeholder="1234567890"
          />
          <ErrorText>
            {errors.confirmaccountnumber && 'Account Number does not match'}
          </ErrorText>
        </div>
      </div>

      {/* Representative Section */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="mb-4 flex items-center gap-2">
          <UserIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Account Representative Details
          </p>
        </div>
        <div className="space-y-4">
          <div>
            <FieldLabel required={!disabled}>
              Representative Full Name
            </FieldLabel>
            <input
              disabled={disabled}
              value={bankAccountForm.reprensentativefullname}
              onChange={(e) =>
                onFormChangeBankAccount(
                  e.target.value,
                  'reprensentativefullname',
                )
              }
              type="text"
              className={inputClass}
              placeholder="John Doe"
            />
            <ErrorText>
              {errors.reprensentativefullname &&
                'Representative Full Name can not be empty'}
            </ErrorText>
          </div>
          <div>
            <FieldLabel required={!disabled}>Phone</FieldLabel>
            <InputMask
              disabled={disabled}
              value={bankAccountForm.phone}
              mask="(999) 999 9999"
              maskPlaceholder={' '}
              onChange={(e) => onFormChangeBankAccount(e.target.value, 'phone')}
              type="text"
              className={inputClass}
              placeholder="(___) ___ ____"
            />
            <ErrorText>
              {errors.phone && 'Phone number can not be empty'}
            </ErrorText>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel required={!disabled}>
                Representative Birthday
              </FieldLabel>
              <DatePicker
                disabled={disabled}
                value={dayjs(
                  bankAccountForm.reprensentativebirthday,
                  'YYYY-MM-DD',
                )}
                format={'MM/DD/YYYY'}
                onChange={(e) =>
                  onFormChangeBankAccount(
                    dayjs(e, 'YYYY-MM-DD'),
                    'reprensentativebirthday',
                  )
                }
                style={{ width: '100%' }}
                size="large"
                placeholder="Select a Representative Birthday"
              />
              <ErrorText>
                {errors.reprensentativebirthday &&
                  'Select a Representative Birthday'}
              </ErrorText>
            </div>
            <div>
              <FieldLabel required={!disabled}>Last 4 Digit of SSN</FieldLabel>
              <input
                disabled={disabled}
                value={bankAccountForm.last4ssn}
                onChange={(e) =>
                  onFormChangeBankAccount(e.target.value, 'last4ssn')
                }
                maxLength={4}
                type="text"
                className={inputClass}
                placeholder="1234"
              />
              <ErrorText>
                {errors.last4ssn && 'Last 4 Digit of SSN can not be empty'}
              </ErrorText>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Address */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="mb-1 flex items-center gap-2">
          <MapPinIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Legal Address
          </p>
        </div>
        <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
          Please enter the address for legal documents, including tax forms and
          1099s, etc.
        </p>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-4">
            <div className="sm:col-span-3">
              <FieldLabel>Street Name</FieldLabel>
              <AddressAutocomplete
                disabled={disabled}
                value={bankAccountForm.address}
                onChange={(e) => !disabled && onAddressSelect(e)}
              />
              <ErrorText>
                {errors.address && 'Address can not be empty'}
              </ErrorText>
            </div>
            <div>
              <FieldLabel>Apt / Suite</FieldLabel>
              <input
                disabled={disabled}
                value={bankAccountForm.apartment}
                onChange={(e) =>
                  onFormChangeBankAccount(e.target.value, 'apartment')
                }
                type="text"
                className={inputClass}
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-4">
            <div className="sm:col-span-2">
              <FieldLabel>City</FieldLabel>
              <input
                disabled={disabled}
                value={bankAccountForm.city}
                onChange={(e) =>
                  onFormChangeBankAccount(e.target.value, 'city')
                }
                type="text"
                className={inputClass}
              />
              <ErrorText>{errors.city && 'City can not be empty'}</ErrorText>
            </div>
            <div>
              <FieldLabel>State</FieldLabel>
              <input
                disabled={disabled}
                value={bankAccountForm.state}
                onChange={(e) =>
                  onFormChangeBankAccount(e.target.value, 'state')
                }
                type="text"
                className={inputClass}
              />
              <ErrorText>{errors.state && 'State can not be empty'}</ErrorText>
            </div>
            <div>
              <FieldLabel>Zip Code</FieldLabel>
              <input
                disabled={disabled}
                value={bankAccountForm.postalcode}
                onChange={(e) =>
                  onFormChangeBankAccount(e.target.value, 'postalcode')
                }
                type="text"
                className={inputClass}
              />
              <ErrorText>
                {errors.postalcode && 'Zip Code can not be empty'}
              </ErrorText>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {loading.listLoading ? (
        <>
          <div className="rounded-md border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center gap-3">
              <SkeletonBlock className="h-10 w-10 !rounded-md" />
              <SkeletonBlock className="h-5 w-40" />
            </div>
            <div className="mt-4 space-y-3">
              <SkeletonBlock className="h-14 w-full !rounded-md" />
              <SkeletonBlock className="h-14 w-full !rounded-md" />
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center gap-3">
              <SkeletonBlock className="h-10 w-10 !rounded-md" />
              <SkeletonBlock className="h-5 w-40" />
            </div>
            <div className="mt-4 space-y-3">
              <SkeletonBlock className="h-20 w-full !rounded-md" />
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Credit Cards */}
          <SectionCard
            icon={CreditCardIcon}
            gradient="from-blue-500 to-indigo-600"
            title="Credit Cards"
            subtitle="View and manage all your credit cards in one place."
            action={
              <PrimaryButton
                onClick={onClickAddCreditCard}
                disabled={loading.upsertLoading}
              >
                <PlusIcon className="h-4 w-4" />
                Add Credit Card
              </PrimaryButton>
            }
          >
            {creditCardList.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-slate-300 px-6 py-10 text-center dark:border-slate-600">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-slate-100 text-slate-400 dark:bg-slate-700/60 dark:text-slate-500">
                  <CreditCardIcon className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    No credit cards yet.
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Add a card to start using it for payments.
                  </p>
                </div>
              </div>
            ) : (
              <ul className="space-y-3">
                {creditCardList.map((card, cardIdx) => (
                  <li
                    key={cardIdx}
                    className="flex flex-col items-start gap-3 rounded-md border border-slate-200 bg-slate-50/60 p-4 transition hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900/30 dark:hover:border-slate-600 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-10 w-14 items-center justify-center rounded-md bg-white p-1 shadow-sm ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
                        <img
                          src={cardImage(card.brand)}
                          alt={card.brand || 'Card'}
                          className="max-h-full max-w-full"
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            •••• {card.last4}
                          </p>
                          {card.isdefault && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                              <CheckCircleIcon className="h-3 w-3" />
                              Default
                            </span>
                          )}
                        </div>
                        {card.brand && (
                          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            {card.brand}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                          Default
                        </span>
                        <AntSwitch
                          disabled={loading.upsertLoading}
                          checked={card.isdefault}
                          onChange={(val) =>
                            onChangeDefaultCreditCard(card, val, cardIdx)
                          }
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => onClickDeleteCreditCard(card)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-rose-600 transition hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10"
                        title="Remove card"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          {/* Bank Accounts */}
          <SectionCard
            icon={BanknotesIcon}
            gradient="from-emerald-500 to-teal-600"
            title="Bank Accounts"
            subtitle="This is the bank account will use for payouts."
            action={
              <PrimaryButton
                onClick={onClickAddBankAccount}
                disabled={loading.upsertLoading}
              >
                <PlusIcon className="h-4 w-4" />
                Add Bank Account
              </PrimaryButton>
            }
          >
            {bankAccountList.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-slate-300 px-6 py-10 text-center dark:border-slate-600">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-slate-100 text-slate-400 dark:bg-slate-700/60 dark:text-slate-500">
                  <BuildingLibraryIcon className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    No bank accounts yet.
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Add a bank account to receive payouts.
                  </p>
                </div>
              </div>
            ) : (
              <ul className="space-y-3">
                {bankAccountList.map((account, accountIdx) => (
                  <li
                    key={accountIdx}
                    className="rounded-md border border-slate-200 bg-slate-50/60 p-4 transition hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900/30 dark:hover:border-slate-600"
                  >
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex items-start gap-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm">
                          <BuildingLibraryIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                              {account.accountholdername}
                            </p>
                            <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-700/60 dark:text-slate-300">
                              {account.accounttype === 10
                                ? 'Company'
                                : 'Individual'}
                            </span>
                            {account.isdefault && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                                <CheckCircleIcon className="h-3 w-3" />
                                Default
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                            <span>
                              Routing{' '}
                              <span className="font-mono text-slate-700 dark:text-slate-200">
                                #{account.routingnumber}
                              </span>
                            </span>
                            <span>
                              Account{' '}
                              <span className="font-mono text-slate-700 dark:text-slate-200">
                                #{account.accountnumber}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="mr-2 flex items-center gap-2">
                          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                            Default
                          </span>
                          <AntSwitch
                            disabled={loading.upsertLoading}
                            checked={account.isdefault}
                            onChange={(val) =>
                              onChangeDefaultBankAccount(
                                account,
                                val,
                                accountIdx,
                              )
                            }
                          />
                        </div>
                        <SecondaryButton
                          onClick={() => onClickConnectStripe(account)}
                        >
                          <LinkIcon className="h-4 w-4" />
                          Connect Stripe
                        </SecondaryButton>
                        <SecondaryButton
                          onClick={() => onClickViewBankAccount(account)}
                        >
                          <EyeIcon className="h-4 w-4" />
                          View
                        </SecondaryButton>
                        <SecondaryButton
                          onClick={() => onClickEditBankAccount(account)}
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                          Edit
                        </SecondaryButton>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </>
      )}

      {/* Upsert Bank Account Modal */}
      <TWModal
        open={dialogs.upsertBankAccount}
        onClose={onCloseUpsertBankAccount}
        size="2xl"
        showCloseButton
        dismissible={false}
        bodyClassName="p-0"
        footer={
          <div className="flex items-center justify-end gap-2">
            {bankAccountForm.id && (
              <DangerIconButton
                disabled={loading.deleteLoading}
                loading={loading.deleteLoading}
                onClick={() => onClickDeleteBankAccount(bankAccountForm)}
              />
            )}
            <SecondaryButton
              disabled={loading.upsertLoading}
              onClick={onCloseUpsertBankAccount}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              disabled={loading.upsertLoading}
              loading={loading.upsertLoading}
              onClick={onFormSubmitBankAccount}
            >
              {bankAccountForm.id ? 'Save Changes' : 'Add Bank Account'}
            </PrimaryButton>
          </div>
        }
      >
        <div className="flex items-start gap-3 border-b border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40">
          <IconBadge icon={BuildingLibraryIcon} tone="emerald" size="md" />
          <div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {bankAccountForm.id ? 'Edit Bank Account' : 'Add Bank Account'}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Payout destination for this company.
            </p>
          </div>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-6">
          {renderBankAccountFields(false)}
        </div>
      </TWModal>

      {/* View Bank Account Modal */}
      <TWModal
        open={dialogs.viewBankAccount}
        onClose={() =>
          setDialogs((prev) => ({ ...prev, viewBankAccount: false }))
        }
        size="2xl"
        showCloseButton
        dismissible={false}
        bodyClassName="p-0"
        footer={
          <div className="flex items-center justify-end gap-2">
            <SecondaryButton
              onClick={() =>
                setDialogs((prev) => ({ ...prev, viewBankAccount: false }))
              }
            >
              Close
            </SecondaryButton>
          </div>
        }
      >
        <div className="flex items-start gap-3 border-b border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40">
          <IconBadge icon={EyeIcon} tone="slate" size="md" />
          <div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Bank Account Details
            </h3>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Read-only view of this bank account.
            </p>
          </div>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-6">
          {renderBankAccountFields(true)}
        </div>
      </TWModal>

      {/* Upsert Credit Card Modal */}
      <TWModal
        open={dialogs.upsertCreditCard}
        onClose={onCloseUpsertCreditCard}
        size="xl"
        showCloseButton
        dismissible={false}
        bodyClassName="p-0"
        footer={null}
      >
        <div className="flex items-start gap-3 border-b border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40">
          <IconBadge icon={CreditCardIcon} tone="blue" size="md" />
          <div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Add Credit Card
            </h3>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Save a card for future charges.
            </p>
          </div>
        </div>
        <div className="space-y-5 px-6 py-6">
          <div>
            <FieldLabel required>Credit Card Number</FieldLabel>
            <input
              value={creditCardForm.createcardmodel?.number}
              onChange={(e) => onFormChangeCreditCard(e.target.value, 'number')}
              type="text"
              maxLength={16}
              minLength={15}
              className={inputClass}
              placeholder="1234 5678 9012 3456"
            />
            <ErrorText>
              {errors.number && 'Please enter a valid credit card number'}
            </ErrorText>
          </div>
          <div className="grid gap-4 sm:grid-cols-4">
            <div>
              <FieldLabel required>Exp Year</FieldLabel>
              <Select
                allowClear
                size="large"
                style={{ width: '100%' }}
                placeholder="Year"
                value={creditCardForm.createcardmodel?.expyear || undefined}
                onChange={(val) => onFormChangeCreditCard(val, 'expyear')}
                options={expYearOptions()}
              />
              <ErrorText>
                {errors.expyear && 'Expyear can not be empty'}
              </ErrorText>
            </div>
            <div>
              <FieldLabel required>Exp Month</FieldLabel>
              <Select
                allowClear
                size="large"
                style={{ width: '100%' }}
                placeholder="Month"
                value={creditCardForm.createcardmodel?.expmonth || undefined}
                onChange={(val) => onFormChangeCreditCard(val, 'expmonth')}
                options={expMonthOptions()}
              />
              <ErrorText>
                {errors.expmonth && 'Expmonth can not be empty'}
              </ErrorText>
            </div>
            <div>
              <FieldLabel required>CVC</FieldLabel>
              <input
                value={creditCardForm.createcardmodel?.cvc}
                onChange={(e) => onFormChangeCreditCard(e.target.value, 'cvc')}
                type="text"
                maxLength={4}
                className={inputClass}
                placeholder="123"
              />
              <ErrorText>{errors.cvc && 'Please enter a valid CVC'}</ErrorText>
            </div>
            <div>
              <FieldLabel required>Zip Code</FieldLabel>
              <input
                value={creditCardForm.createcardmodel?.zipcode}
                onChange={(e) =>
                  onFormChangeCreditCard(e.target.value, 'zipcode')
                }
                type="text"
                maxLength={5}
                className={inputClass}
                placeholder="10001"
              />
              <ErrorText>
                {errors.zipcode && 'Please enter a valid zip code'}
              </ErrorText>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40">
          <SecondaryButton
            disabled={loading.upsertLoading}
            onClick={onCloseUpsertCreditCard}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            disabled={loading.upsertLoading}
            loading={loading.upsertLoading}
            onClick={onFormSubmitCreditCard}
          >
            Add Credit Card
          </PrimaryButton>
        </div>
      </TWModal>

      <ModalDelete
        title="Delete Bank Account"
        loading={loading.deleteLoading}
        open={dialogs.deleteBankAccount}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, deleteBankAccount: false }))
        }
        onOk={onConfirmDeleteBankAccount}
        item={{
          name: deletedBankAccount.accountnumber,
          id: deletedBankAccount.id,
        }}
      />
      <ModalDelete
        title="Delete Credit Card"
        loading={loading.deleteLoading}
        open={dialogs.deleteCreditCard}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, deleteCreditCard: false }))
        }
        onOk={onConfirmDeleteCreditCard}
        item={{
          name: deletedCreditCard.last4,
          id: deletedCreditCard.id,
        }}
      />
    </div>
  );
};

export default AccountInformation;
